import { Select } from '../../../../components/inputs/Select';

interface TargetOption {
  value: string;
  label: string;
}

/**
 * One of a survey's two coverage lists — SEARCH, THEN A TABLE (the Share dialog's shape, by
 * product call 13 Oct 2026; it was a chip picker): choosing a name ADDS A ROW, and each row is
 * taken back off with its own Remove.
 *
 * An EMPTY list means ALL, so the table is never blank: with nothing added it says so in one
 * muted row rather than reading as a list that failed to load.
 */
export function SurveyTargetPicker({
  id,
  label,
  description,
  placeholder,
  column,
  allLabel,
  value,
  onChange,
  options,
  offered,
  loading,
  disabled,
  error,
}: {
  /** The picker's id — the server's field name, kebab-cased, so a rejected write can focus it. */
  id: string;
  label: string;
  description: string;
  placeholder: string;
  /** The table's one data heading. */
  column: string;
  /** What the empty list means, e.g. "All positions". */
  allLabel: string;
  value: string[];
  onChange: (next: string[]) => void;
  options: TargetOption[];
  /**
   * Narrows what the picker OFFERS, never what the table names: a row added before the list
   * narrowed keeps its name and its Remove. Absent offers every option.
   */
  offered?: ReadonlySet<string>;
  loading: boolean;
  disabled: boolean;
  error?: string;
}) {
  const labelOf = (target: string) => options.find((o) => o.value === target)?.label ?? '—';

  return (
    <div className="flex flex-col gap-2">
      <Select
        id={id}
        label={label}
        description={description}
        placeholder={placeholder}
        // Never holds a value: choosing one adds a row below and the box is ready for the next.
        value=""
        onChange={(next) => {
          if (!next || value.includes(next)) return;
          onChange([...value, next]);
        }}
        options={options.filter((o) => !value.includes(o.value) && (offered?.has(o.value) ?? true))}
        remote
        loading={loading}
        disabled={disabled}
        error={error}
      />

      <div className="modern-scrollbar overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full table-auto">
          <thead className="bg-slate-100">
            <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
              <th className="px-3 py-2 text-left">{column}</th>
              <th className="w-28 border-l border-slate-200 px-3 py-2 text-right">Action</th>
            </tr>
          </thead>

          <tbody>
            {value.length === 0 ? (
              <tr className="border-t border-slate-100">
                <td colSpan={2} className="px-3 py-2 align-middle text-sm text-slate-400">
                  {allLabel}
                </td>
              </tr>
            ) : (
              value.map((target) => (
                <tr key={target} className="border-t border-slate-100">
                  <td className="px-3 py-2 align-middle text-sm text-slate-600">{labelOf(target)}</td>
                  <td className="border-l border-slate-100 px-3 py-2 text-right align-middle">
                    <button
                      id={`${id}-remove-${target}`}
                      type="button"
                      className="text-xs font-medium text-red-600 hover:underline"
                      onClick={() => onChange(value.filter((x) => x !== target))}
                      disabled={disabled}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
